/**
 * /vod/:id — FapHouse VOD detail page
 * SEO: title, description, og:tags, canonical, JSON-LD VideoObject schema
 * Embeds the FapHouse player via affiliate URL in an iframe-like container.
 * Related videos fetched from FapHouse API using the video's first tag.
 */
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Chip, Skeleton, Button, Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import { fetchFapHouseVideos, CATEGORY_KEYWORD_MAP } from "../contexts/FapHouseContext";

const API = "https://fh-wgt.com/api/request/v1/affiliate_1";
const CAMPAIGN = "ai.oFZ";

// ─── SEO helpers ─────────────────────────────────────────────────────────────
const SEOHead = ({ title, description, canonical, thumbnail, video }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        prop ? el.setAttribute("property", name) : el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, true);
    }
    if (title) {
      setMeta("og:title", title, true);
      setMeta("twitter:title", title);
    }
    if (thumbnail) {
      setMeta("og:image", thumbnail, true);
      setMeta("twitter:image", thumbnail);
    }
    setMeta("og:type", "video.other", true);
    setMeta("twitter:card", "summary_large_image");
    if (video?.url) setMeta("og:video", video.url, true);

    if (canonical) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;
      setMeta("og:url", canonical, true);
    }

    // JSON-LD VideoObject schema
    if (video) {
      const existing = document.getElementById("vod-jsonld");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "vod-jsonld";
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: video.title,
        description: video.title,
        thumbnailUrl: video.thumbnailUrl,
        contentUrl: video.url,
        embedUrl: video.url,
        duration: video.duration ? `PT${video.duration}S` : undefined,
        uploadDate: new Date().toISOString().split("T")[0],
        isFamilyFriendly: false,
      });
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById("vod-jsonld");
      if (s) s.remove();
    };
  }, [title, description, canonical, thumbnail, video]);

  return null;
};

// ─── Styled components ────────────────────────────────────────────────────────
const PlayerWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  maxWidth: 800,
  aspectRatio: "16/9",
  background: "#0a0a0a",
  borderRadius: 10,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

const Thumbnail = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const PlayOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.2s",
  "&:hover": { background: "rgba(0,0,0,0.2)" },
});

const PlayBtn = styled(Box)({
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.15)",
  backdropFilter: "blur(4px)",
  border: "2px solid rgba(255,255,255,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.15s",
  "&:hover": { transform: "scale(1.12)" },
});

const RelatedCard = styled(Box)({
  position: "relative",
  borderRadius: 8,
  overflow: "hidden",
  cursor: "pointer",
  "&:hover img": { transform: "scale(1.05)" },
  "&:hover .rel-overlay": { opacity: 1 },
});

const RelThumb = styled("img")({
  width: "100%",
  height: 110,
  objectFit: "cover",
  display: "block",
  transition: "transform 0.25s",
});

const RelOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.2s",
});

const fmtDuration = (secs) => {
  if (!secs) return null;
  const s = parseInt(secs, 10);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function VodPage() {
  const { id } = useParams(); // e.g. "fh_6655101" or "latina-cam-girl-fh_6655101" (slugified)
  // Extract the raw fh_ id from either format
  const rawId = id?.includes('fh_') ? 'fh_' + id.split('fh_').pop() : id;
  const navigate = useNavigate();

  const [video, setVideo]       = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [playing, setPlaying]   = useState(false);

  // Extract numeric FapHouse ID from our "fh_XXXXX" slug
  const fhId = rawId?.replace("fh_", "");

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlaying(false);
    setVideo(null);
    setRelated([]);
    setLoading(true);

    const load = async () => {
      try {
        // FapHouse doesn't have a single-video endpoint — fetch by ID via search trick
        // We fetch trending and scan, OR directly use the stored data from context.
        // Best approach: fetch a batch and find our video, fall back to constructing from ID.
        const params = {
          language: "en",
          orientation: "straight",
          thumbsMode: "big",
          quantity: 100,
          utmCampaign: CAMPAIGN,
        };
        const res = await axios.get(API, { params });
        const all = res.data?.videos || [];
        const found = all.find((v) => String(v.id) === String(fhId));

        if (found) {
          const normalized = {
            id: `fh_${found.id}`,
            title: found.title,
            thumbnailUrl: found.image2xUrl || found.imageUrl,
            previewUrl: found.previewUrl,
            url: found.url,
            duration: found.duration,
            quality: found.quality,
            accessType: found.accessType,
            tags: found.payload?.tags || [],
          };
          setVideo(normalized);

          // Fetch related by first tag
          const keyword = normalized.tags?.[0] || "popular";
          const relVideos = await fetchFapHouseVideos({ keyword, quantity: 12 });
          setRelated(relVideos.filter((v) => v.id !== normalized.id).slice(0, 8));
        } else {
          // Video not in trending — construct minimal shell for the page
          // Google still gets the sitemap data; page shows a generic player link
          setVideo({
            id,
            title: "Watch this video on InstaCamGirls",
            thumbnailUrl: null,
            url: null,
            duration: null,
            tags: [],
          });
          // Still load related
          const relVideos = await fetchFapHouseVideos({ quantity: 12 });
          setRelated(relVideos.slice(0, 8));
        }
      } catch (e) {
        console.error("VodPage load error:", e);
      } finally {
        setLoading(false);
      }
    };

    if (fhId) load();
  }, [id, fhId]);

  const handlePlay = () => {
    if (video?.url) {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
    setPlaying(true);
  };

  const pageTitle = video
    ? `${video.title} — Watch Free on InstaCamGirls`
    : "Watch Video — InstaCamGirls";
  const pageDesc = video
    ? `Watch "${video.title}" free on InstaCamGirls.${video.duration ? ` ${fmtDuration(video.duration)} min video.` : ""} Browse thousands of cam girl videos, live shows, and exclusive clips.`
    : "Watch free cam girl videos on InstaCamGirls.";
  const canonical = `https://instacamgirls.com/vod/${id}`; // keep original slug in canonical

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0d0d0d", color: "#fff", px: { xs: 2, md: 4 }, py: 3 }}>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonical={canonical}
        thumbnail={video?.thumbnailUrl}
        video={video}
      />

      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ color: "#aaa", mb: 2, textTransform: "none", fontSize: "0.85rem" }}
      >
        Back
      </Button>

      <Grid container spacing={3}>
        {/* Left — player + info */}
        <Grid item xs={12} md={8}>
          {/* Player */}
          {loading ? (
            <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: "16/9", borderRadius: 2 }} />
          ) : (
            <PlayerWrapper onClick={handlePlay}>
              {video?.thumbnailUrl ? (
                <Thumbnail src={video.thumbnailUrl} alt={video?.title} />
              ) : (
                <Box sx={{ width: "100%", height: "100%", bgcolor: "#111" }} />
              )}
              <PlayOverlay>
                <PlayBtn>
                  <PlayArrowIcon sx={{ color: "#fff", fontSize: 36 }} />
                </PlayBtn>
              </PlayOverlay>
              {video?.quality && (
                <Box sx={{
                  position: "absolute", top: 8, right: 8,
                  bgcolor: video.quality === "4k" ? "#9C27B0" : "#1565C0",
                  color: "#fff", fontSize: "0.6rem", fontWeight: 800,
                  px: "6px", py: "2px", borderRadius: 1, textTransform: "uppercase",
                }}>
                  {video.quality}
                </Box>
              )}
              {video?.duration && (
                <Box sx={{
                  position: "absolute", bottom: 40, right: 8,
                  bgcolor: "rgba(0,0,0,0.75)", color: "#fff",
                  fontSize: "0.65rem", px: "5px", py: "1px", borderRadius: 1,
                }}>
                  {fmtDuration(video.duration)}
                </Box>
              )}
            </PlayerWrapper>
          )}

          {/* Watch button */}
          {!loading && video?.url && (
            <Button
              variant="contained"
              fullWidth
              endIcon={<OpenInNewIcon />}
              onClick={handlePlay}
              sx={{
                mt: 1.5, bgcolor: "#e91e8c", "&:hover": { bgcolor: "#c2185b" },
                fontWeight: 700, fontSize: "1rem", borderRadius: 2, py: 1.2,
                textTransform: "none",
              }}
            >
              Watch Full Video Free
            </Button>
          )}

          {/* Title + meta */}
          <Box sx={{ mt: 2 }}>
            {loading ? (
              <>
                <Skeleton width="80%" height={32} sx={{ bgcolor: "#222" }} />
                <Skeleton width="40%" height={20} sx={{ bgcolor: "#222", mt: 1 }} />
              </>
            ) : (
              <>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: { xs: "1.1rem", md: "1.35rem" } }}>
                  {video?.title}
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {video?.quality && (
                    <Chip label={video.quality.toUpperCase()} size="small"
                      sx={{ bgcolor: "#1a1a1a", color: "#aaa", fontSize: "0.7rem" }} />
                  )}
                  {video?.accessType === "free" && (
                    <Chip label="FREE" size="small"
                      sx={{ bgcolor: "#1b5e20", color: "#69f0ae", fontSize: "0.7rem", fontWeight: 700 }} />
                  )}
                  {video?.duration && (
                    <Typography variant="body2" sx={{ color: "#888", fontSize: "0.8rem" }}>
                      {fmtDuration(video.duration)}
                    </Typography>
                  )}
                </Box>

                {/* Tags */}
                {video?.tags?.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.75, mt: 1.5, flexWrap: "wrap" }}>
                    {video.tags.slice(0, 10).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        component={Link}
                        to={`/tag/${tag.toLowerCase().replace(/\s+/g, "-")}`}
                        clickable
                        sx={{ bgcolor: "#1a1a1a", color: "#ccc", fontSize: "0.7rem",
                          "&:hover": { bgcolor: "#333" }, cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>

          <Divider sx={{ my: 3, borderColor: "#222" }} />

          {/* SEO text block */}
          <Box sx={{ color: "#666", fontSize: "0.8rem", lineHeight: 1.7 }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Watch cam girl videos free on InstaCamGirls — the best live cam girl site featuring thousands of performers from Streamate, FapHouse, SinParty, and more.
              Browse by category: <Link to="/category/latina-cam-girls" style={{ color: "#e91e8c" }}>Latina</Link>,{" "}
              <Link to="/category/milf-cam-girls" style={{ color: "#e91e8c" }}>MILF</Link>,{" "}
              <Link to="/category/ebony-cam-girls" style={{ color: "#e91e8c" }}>Ebony</Link>,{" "}
              <Link to="/category/asian-cam-girls" style={{ color: "#e91e8c" }}>Asian</Link>, and more.
            </Typography>
          </Box>
        </Grid>

        {/* Right — related videos */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#ccc" }}>
            Related Videos
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" height={110} sx={{ borderRadius: 1, bgcolor: "#1a1a1a" }} />
                ))
              : related.map((v) => (
                  <RelatedCard
                    key={v.id}
                    component={Link}
                    to={`/vod/${v.id}`}
                    sx={{ textDecoration: "none" }}
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={5}>
                        <Box sx={{ position: "relative", borderRadius: 1, overflow: "hidden" }}>
                          <RelThumb src={v.thumbnailUrl} alt={v.title} />
                          <RelOverlay className="rel-overlay">
                            <PlayArrowIcon sx={{ color: "#fff", fontSize: 28 }} />
                          </RelOverlay>
                          {v.duration && (
                            <Box sx={{
                              position: "absolute", bottom: 4, right: 4,
                              bgcolor: "rgba(0,0,0,0.75)", color: "#fff",
                              fontSize: "0.6rem", px: "4px", borderRadius: 0.5,
                            }}>
                              {fmtDuration(v.duration)}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography sx={{
                          fontSize: "0.75rem", color: "#ccc", lineHeight: 1.35,
                          display: "-webkit-box", WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {v.title}
                        </Typography>
                        {v.quality && (
                          <Chip label={v.quality.toUpperCase()} size="small"
                            sx={{ mt: 0.5, bgcolor: "#1a1a1a", color: "#888", fontSize: "0.6rem", height: 16 }} />
                        )}
                      </Grid>
                    </Grid>
                  </RelatedCard>
                ))
            }
          </Box>

          {/* Link back to live cams */}
          <Box sx={{ mt: 3, p: 2, bgcolor: "#111", borderRadius: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.8rem", color: "#aaa", mb: 1 }}>
              Want to see her live?
            </Typography>
            <Button
              component={Link}
              to="/"
              variant="outlined"
              size="small"
              sx={{ borderColor: "#e91e8c", color: "#e91e8c", textTransform: "none", fontSize: "0.8rem" }}
            >
              Browse Live Cams
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
